import React from "react";
import { BASE_URL } from "../../../config";
import { format, addHours } from 'date-fns';
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import { useGoogleMaps } from "@/context/GoogleMapsContext";
import { useGetList } from "react-admin";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Route, User, AppWindowMac } from "lucide-react";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { grayMapStyle, meters_to_km_velocity } from "@/utils/variables";

const center = {
    lat: 41.32900247531315,
    lng: 19.817979844617586,
};

const polylineOptions = {
    strokeColor: "#0000FF",
    strokeOpacity: 0.8,
    strokeWeight: 4,
    fillColor: "#0000FF",
    clickable: false,
    draggable: false,
    editable: false,
    geodesic: true,
};

const OrderRoute = ({ record }: any) => {
    const mapRef = React.useRef<google.maps.Map | null>(null);
    const { isLoaded, loadError } = useGoogleMaps();

    const [openDialog, setOpenDialog] = React.useState(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [polylines, setPolylines] = React.useState<any>([]);

    const [currentPosition, setCurrentPosition] = React.useState(polylines[0]);
    const [sliderValue, setSliderValue] = React.useState(0);

    const { data: vehicles } = useGetList(
        'gps-track/track-vehicles',
        {
            pagination: { page: 1, perPage: 23 },
            sort: { field: 'date', order: 'DESC' },
            filter: {}
        }
    );

    const getGpsVehicleDetailsId = (event: React.MouseEvent, order: any) => {
        event.preventDefault();
        event.stopPropagation();
        if (vehicles) {

            for (let index = 0; index < vehicles.length; index++) {
                const element = vehicles[index];
                if (element.id === order.vehicle_id) {
                    handleRowClick(order, element);
                }
            }
        }
    }

    const handleRowClick = (order: any, element: any) => {

        setOpenDialog(true);
        setLoading(true);
        setPolylines([]);

        const url = `${BASE_URL}/gps-track/users/${element.gpsData.id}/tracks/${format(new Date(order.pickup_time), 'yyyy-MM-dd')}?startTime=${format(addHours(new Date(order.pickup_time), 0), 'HH:mm:ss')}&endTime=${format(addHours(new Date(order.dropoff_time), 0), 'HH:mm:ss')}&filtered=true`;

        fetch(url, {
            method: 'GET',
            headers: {
                'accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem("access_token")}`,
            }
        }).then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        }).then(data => {
            for (let index = 0; index < data.length; index++) {
                const element = data[index];
                if (element.velocity.groundSpeed > 0) {
                    console.log(element);
                    setOpenDialog(true);
                    setLoading(false);
                    setPolylines((prevPolylines: any) => [...prevPolylines, element]);
                }
            }
        }).catch(error => {
            console.error('There was a problem with the fetch operation:', error);
        }).finally(() => {
            setLoading(false);
        });

    };

    React.useEffect(() => {
        const index = Math.floor((sliderValue / 100) * (polylines.length - 1));
        setCurrentPosition(polylines[index]);
    }, [sliderValue, polylines]);

    const onMapLoad = (map: google.maps.Map) => {
        mapRef.current = map;
    };

    const handleSliderChange = (value: number[]) => {
        setSliderValue(value as unknown as number);
    };

    React.useEffect(() => {
        if (mapRef.current && polylines.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();

            polylines.forEach((point: any) => {
                bounds.extend({
                    lat: point.position.latitude,
                    lng: point.position.longitude,
                });
            });

            mapRef.current.fitBounds(bounds);
        }
    }, [polylines])

    if (loadError) return <p>Error loading Google Maps: {loadError.message}</p>;
    if (!isLoaded) return <p>Loading map...</p>;

    return (
        <>
            <DropdownMenuItem onClick={(e) => getGpsVehicleDetailsId(e, record)} className="cursor-pointer">
                <Route className="w-4 h-4" />
                Track Route
            </DropdownMenuItem>
            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
                <DialogContent className="sm:max-w-[1080px] max-h-[90vh] flex flex-col p-3" onClick={(e) => e.stopPropagation()}>
                    <GoogleMap
                        zoom={13}
                        center={center}
                        mapContainerStyle={{ width: "100%", height: "100vh" }}
                        onLoad={onMapLoad}
                        options={{
                            styles: grayMapStyle,
                            disableDefaultUI: true,
                            zoomControl: true,
                            gestureHandling: "greedy",
                        }}
                    >
                        {polylines.length > 0 && (
                            <>
                                <Marker
                                    position={{ lat: polylines[0].position.latitude, lng: polylines[0].position.longitude }}
                                    label="Start"
                                />
                                <Marker
                                    position={{ lat: polylines[polylines.length - 1].position.latitude, lng: polylines[polylines.length - 1].position.longitude }}
                                    label="End"
                                />
                                {currentPosition && (
                                    <Marker
                                        position={{ lat: currentPosition.position.latitude, lng: currentPosition.position.longitude }}
                                    />
                                )}
                                {polylines?.length >= 2 && (
                                    <Polyline
                                        path={polylines.map((point: any) => ({
                                            lat: point.position.latitude,
                                            lng: point.position.longitude,
                                        }))}
                                        options={polylineOptions}
                                    />
                                )}
                            </>
                        )}
                    </GoogleMap>
                    <div className="bg-white w-full rounded-2xl font-bold">
                        <p className="pb-3">Track Route</p>
                        <Slider
                            value={[sliderValue]}
                            onValueChange={handleSliderChange}
                            max={100}
                            step={1}
                        />
                        <div className="relative pt-4 text-sm text-gray-700">
                            {currentPosition && (
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-1">
                                        <User
                                            className="w-5 h-5"
                                            style={{
                                                color:
                                                    currentPosition?.variables.seatSensor ||
                                                        currentPosition?.variables.pasagjer
                                                        ? "#ff4d4d"
                                                        : "#00ff00",
                                            }}
                                        />
                                        <span className="text-xs">Pasager</span>
                                    </div>

                                    <div className="flex items-center space-x-1">
                                        <AppWindowMac
                                            className="w-5 h-5"
                                            style={{
                                                color:
                                                    currentPosition?.variables.taxiMeter || currentPosition?.variables.taximeter
                                                        ? "#ff4d4d"
                                                        : "#00ff00",
                                            }}
                                        />
                                        <span className="text-xs">Taximeter</span>
                                    </div>

                                    {currentPosition?.utc != null && (
                                        <div className="ml-auto text-xs text-gray-600">
                                            <span className="font-medium">Time:</span>{" "}
                                            {format(currentPosition?.utc, "dd MMMM yyyy HH:mm")}{" "}
                                        </div>
                                    )}

                                    <div className="ml-auto text-xs text-gray-600">
                                        <span className="font-medium">Speed:</span>{" "}
                                        {!isNaN(currentPosition.variables.speed) ? (Math.round(
                                            currentPosition.variables.speed * meters_to_km_velocity,
                                        )) : 'N/A'}{" "}
                                        km/h
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default OrderRoute;